import { useState, useCallback } from "react";
import { getOneOmsBaseUrl } from "../constants/omsRegionConfig";
import tokenUtils from "../utils/tokenUtils";

const MODIFICATION_ACTION = {
  accept: "ACCEPT",
  reject: "DISCARD",
};

const getHeaders = () => {
  const token = tokenUtils.getToken();

  return {
    accept: "application/json",
    "accept-language": "en-US,en;q=0.7",
    "cache-control": "no-cache",
    "content-type": "application/json",
    origin: "https://adminsupport.infinitylearn.com",
    pragma: "no-cache",
    priority: "u=1, i",
    referer: "https://adminsupport.infinitylearn.com/",
    "auth-token": token || "",
    "x-platform": "web",
    "x-source-id": "1",
  };
};

const parseResponse = async (response) => {
  const text = await response.text();
  return text ? JSON.parse(text) : { success: true };
};

const getErrorMessage = (errorJson, fallbackMessage) =>
  errorJson?.message || errorJson?.detail || fallbackMessage;

const getProposalIdFromSummary = (summary) =>
  summary?.data?.proposal_order?.id;


const getOrderModificationSummary = async (orderId) => {
  const endpoint = `${getOneOmsBaseUrl()}/get-order-modification-summary/${orderId}`;

  const response = await fetch(endpoint, {
    method: "GET",
    headers: getHeaders(),
  });

  if (!response.ok) {
    const errorJson = await response.json().catch(() => ({}));
    throw new Error(
      getErrorMessage(
        errorJson,
        "Failed to fetch order modification summary. Please try again.",
      ),
    );
  }

  return parseResponse(response);
};

const updateOrderModificationRequest = async (action, orderId, proposalId) => {
  const operation = MODIFICATION_ACTION[action];
  const endpoint = `${getOneOmsBaseUrl()}/update-order-modification-request`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({
      order_id: orderId,
      proposal_id: proposalId,
      operation,
    }),
  });

  if (!response.ok) {
    const errorJson = await response.json().catch(() => ({}));
    throw new Error(
      getErrorMessage(
        errorJson,
        `Failed to ${operation} the modification request. Please try again.`,
      ),
    );
  }

  return parseResponse(response);
};

const callModificationApi = async (action, orderId) => {
  try {
    const summary = await getOrderModificationSummary(orderId);
    const proposalId = getProposalIdFromSummary(summary);

    if (!proposalId) {
      throw new Error("Unable to find proposal id for this modification request.");
    }

    return updateOrderModificationRequest(action, orderId, proposalId);
  } catch (err) {
    throw err;
  }
};

export const useOrderModificationActions = ({ onSuccess, onError } = {}) => {
  const [loadingMap, setLoadingMap] = useState({});

  const setOrderLoading = (orderId, actionType) =>
    setLoadingMap((prev) => ({ ...prev, [orderId]: actionType }));

  const handleAction = useCallback(
    async (action, order) => {
      const { order_id } = order;
      setOrderLoading(order_id, action);

      try {
        const result = await callModificationApi(action, order_id);
        onSuccess?.(action, order, result);
      } catch (err) {
        console.error(`[useOrderModificationActions] ${action} failed:`, err);
        onError?.(action, order, err.message);
      } finally {
        setOrderLoading(order_id, null);
      }
    },
    [onSuccess, onError],
  );

  const acceptModification = useCallback(
    (order) => handleAction("accept", order),
    [handleAction],
  );

  const rejectModification = useCallback(
    (order) => handleAction("reject", order),
    [handleAction],
  );

  const isOrderLoading = (orderId) => loadingMap[orderId] || null;

  return { acceptModification, rejectModification, isOrderLoading };
};
